import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Blobs, FloatingBubbles, PawPrint } from "../components/Effects";
import { RippleButton } from "../components/RippleButton";
import { Heart, ShieldCheck, Home, Sparkles, Smile, Target, Eye, Quote, ArrowRight, Award } from "lucide-react";
import { useBooking } from "../context/BookingContext";

export default function AboutUs() {
  const navigate = useNavigate();
  const { openBookingModal } = useBooking();

  const handleBook = (serviceName = "Doorstep Grooming") => {
    openBookingModal(serviceName);
  };

  const values = [
    {
      icon: Heart,
      title: "Love First",
      desc: "Every pet is treated with the same affection, patience, and respect that we would give our own.",
      color: "from-pink-500/20 to-rose-500/10",
      iconColor: "text-rose-500",
      borderColor: "border-rose-200"
    },
    {
      icon: ShieldCheck,
      title: "Trust Always",
      desc: "We value the confidence pet parents place in us and work every day to earn it through honesty, reliability, and quality service.",
      color: "from-amber-500/20 to-orange-500/10",
      iconColor: "text-amber-600",
      borderColor: "border-amber-200"
    },
    {
      icon: Home,
      title: "Comfort Matters",
      desc: "We believe pets feel happiest in familiar surroundings, which is why we bring the grooming salon to their home.",
      color: "from-sky-500/20 to-blue-500/10",
      iconColor: "text-sky-600",
      borderColor: "border-sky-200"
    },
    {
      icon: Sparkles,
      title: "Quality Without Compromise",
      desc: "From our grooming products to our equipment and techniques, we focus on delivering a safe, hygienic, and premium experience.",
      color: "from-emerald-500/20 to-teal-500/10",
      iconColor: "text-emerald-600",
      borderColor: "border-emerald-200"
    },
    {
      icon: Smile,
      title: "Keep It Simple",
      desc: "Booking, grooming, and caring for your pet should be effortless. We aim to make every step smooth and worry-free.",
      color: "from-purple-500/20 to-indigo-500/10",
      iconColor: "text-purple-600",
      borderColor: "border-purple-200"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFF8F2] text-woofsy-dark overflow-hidden">
      <Navbar onBook={handleBook} />

      {/* Decorative background effects */}
      <Blobs />
      <FloatingBubbles count={12} />

      {/* Floating Paw Prints */}
      {[["6%", "14%", 0], ["90%", "22%", 1.2], ["78%", "68%", 2.4], ["10%", "75%", 3.6]].map(([l, t, d], i) => (
        <motion.div
          key={i}
          className="absolute text-woofsy-primary/15 w-8 h-8 pointer-events-none z-0"
          style={{ left: l, top: t }}
          animate={{ y: [0, -14, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 6, delay: d, repeat: Infinity, ease: "easeInOut" }}
        >
          <PawPrint className="w-8 h-8" />
        </motion.div>
      ))}

      {/* Hero Section */}
      <section className="relative pt-32 pb-12 px-5 sm:px-8 max-w-5xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs sm:text-sm font-semibold text-woofsy-dark shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-woofsy-primary animate-pulse" />
            About Woofsy • Our Story & Values
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-6xl leading-tight text-woofsy-dark">
            About <span className="text-gradient">Woofsy</span>
          </h1>
          <p className="mt-2 text-xl sm:text-2xl font-heading font-bold text-woofsy-primary tracking-wide">
            Less Waiting. More Wagging.
          </p>

          <p className="mt-4 text-base sm:text-lg text-woofsy-dark/75 max-w-2xl mx-auto leading-relaxed">
            Woofsy was born from a simple belief that pet grooming should be easy, comfortable, and filled with care.
          </p>
        </motion.div>
      </section>

      {/* Main Story Section */}
      <section className="py-8 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass rounded-3xl p-6 sm:p-9 shadow-xl border border-white/80 space-y-5 text-base sm:text-lg text-woofsy-dark/80 leading-relaxed relative overflow-hidden"
        >
          <div className="absolute -top-10 -right-10 w-36 h-36 bg-woofsy-primary/10 rounded-full blur-xl pointer-events-none" />

          <p>
            As pet lovers, we've seen how stressful grooming can be. Long drives, crowded salons, unfamiliar environments, and anxious pets often turn what should be a positive experience into a difficult one for both pets and their families.
          </p>

          <p className="font-heading font-bold text-xl sm:text-2xl text-woofsy-primary py-2 border-l-4 border-woofsy-primary pl-4 bg-woofsy-primary/5 rounded-r-xl">
            We knew there had to be a better way.
          </p>

          <p>
            That's why we created <strong>Woofsy</strong>. We bring professional pet grooming right to your doorstep so your furry companion can enjoy a familiar, safe, and relaxed environment while receiving the care they deserve.
          </p>

          <p>
            For us, grooming is about much more than a fresh haircut or a shiny coat. It's about building trust, treating every pet with patience, and creating an experience that keeps tails wagging from start to finish.
          </p>

          <p>
            Every groomer on our team is trained to handle pets with kindness and respect. We use high-quality, pet-safe products and maintain strict hygiene standards because your pet's health and comfort will always come first.
          </p>

          <p>
            Whether it's your puppy's first grooming session or your dog's regular spa & grooming routine, we're here to make every visit simple, stress-free, and enjoyable.
          </p>

          <div className="pt-4 border-t border-woofsy-dark/10 space-y-3">
            <p>
              Woofsy is still at the beginning of its journey, and we're grateful to every family that chooses to trust us. Every booking motivates us to improve, grow, and deliver an even better experience.
            </p>
            <p className="font-heading font-bold text-woofsy-dark text-lg sm:text-xl text-woofsy-primary">
              Thank you for welcoming us into your home and trusting us with a member of your family.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-10 px-5 sm:px-8 max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Mission Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.4 }}
            className="glass rounded-3xl p-6 sm:p-8 shadow-lg border border-woofsy-primary/20 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
              <Target className="w-24 h-24 text-woofsy-primary" />
            </div>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-woofsy-primary/15 flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-woofsy-primary" />
              </div>
              <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-woofsy-dark mb-3">
                Our Mission
              </h3>
              <p className="text-base text-woofsy-dark/80 leading-relaxed">
                To make professional pet grooming convenient, trustworthy, and accessible by delivering high-quality doorstep services that put every pet's comfort, safety, and happiness first.
              </p>
            </div>
          </motion.div>

          {/* Vision Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.4 }}
            className="glass rounded-3xl p-6 sm:p-8 shadow-lg border border-woofsy-secondary/30 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
              <Eye className="w-24 h-24 text-woofsy-secondary" />
            </div>
            <div>
              <div className="w-12 h-12 rounded-2xl bg-sky-500/15 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-heading font-extrabold text-xl sm:text-2xl text-woofsy-dark mb-3">
                Our Vision
              </h3>
              <p className="text-base text-woofsy-dark/80 leading-relaxed">
                To become India's most trusted doorstep pet care brand by creating experiences that pet parents love and pets genuinely enjoy.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-10 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-woofsy-primary">What Defines Us</span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-4xl text-woofsy-dark mt-1">
              Our Core Values
            </h2>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                whileHover={{ y: -5 }}
                className={`glass rounded-3xl p-6 border ${v.borderColor} shadow-md relative overflow-hidden bg-gradient-to-br ${v.color}`}
              >
                <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center mb-4 shadow-sm">
                  <Icon className={`w-6 h-6 ${v.iconColor}`} />
                </div>
                <h3 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark mb-2">
                  {v.title}
                </h3>
                <p className="text-sm sm:text-base text-woofsy-dark/75 leading-relaxed">
                  {v.desc}
                </p>
              </motion.div>
            );
          })}

          {/* Decorative CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="glass rounded-3xl p-6 border border-woofsy-primary/30 shadow-md bg-gradient-to-br from-woofsy-primary/20 to-amber-500/10 flex flex-col justify-center items-center text-center"
          >
            <div className="w-12 h-12 rounded-full bg-woofsy-primary text-white flex items-center justify-center mb-3 shadow-md">
              <Award className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-bold text-lg text-woofsy-dark mb-1">
              Ready to Pamper Your Pet?
            </h3>
            <p className="text-xs sm:text-sm text-woofsy-dark/75 mb-4">
              Experience doorstep grooming at its best in Pune.
            </p>
            <RippleButton variant="primary" className="px-5 py-2 text-xs sm:text-sm font-semibold" onClick={handleBook}>
              Book Grooming Now
            </RippleButton>
          </motion.div>
        </div>
      </section>

      {/* A Note From Our Founders (Fresh Premium Cyan & Sky-Blue Ocean Glass Theme) */}
      <section className="py-10 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative rounded-[2.5rem] p-7 sm:p-10 shadow-[0_20px_60px_rgba(14,165,233,0.15)] border-2 border-sky-300/60 bg-gradient-to-br from-[#E0F2FE] via-white to-[#F0F9FF] overflow-hidden"
        >
          {/* Subtle glowing ocean ambient lighting */}
          <div className="absolute -top-16 -left-16 w-56 h-56 bg-sky-400/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 bg-teal-300/25 rounded-full blur-3xl pointer-events-none" />

          {/* Background decorative quote emblem */}
          <div className="absolute top-4 right-6 opacity-10 pointer-events-none">
            <Quote className="w-36 h-36 text-sky-600" />
          </div>

          <div className="relative z-10 space-y-4">
            <div className="inline-flex items-center gap-2 bg-sky-500/15 text-sky-700 border border-sky-400/40 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
              <Quote className="w-4 h-4 text-sky-600" /> A Note From Our Founders
            </div>

            <h3 className="font-heading font-extrabold text-2xl sm:text-4xl text-slate-800 tracking-tight leading-snug">
              Woofsy started with one simple idea.
            </h3>

            <blockquote className="text-base sm:text-xl text-slate-800 font-semibold italic leading-snug border-l-4 border-sky-500 bg-sky-500/10 p-4 rounded-r-2xl my-2 shadow-sm">
              "If we can make one pet feel more comfortable and one pet parent feel more at ease, then we're building something worthwhile."
            </blockquote>

            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              We know that every wagging tail, every happy purr, and every smile after a grooming session is built on trust. That trust means everything to us.
            </p>

            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              We're excited to be part of your pet's journey, and we promise to treat every pet who meets Woofsy with the love, care, and respect they deserve.
            </p>

            <div className="pt-5 border-t border-sky-200/80 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-heading font-bold text-xl sm:text-2xl text-sky-600 tracking-wide">
                  Less Waiting. More Wagging.
                </p>
                <p className="text-xs font-medium text-slate-500">The Founders of Woofsy</p>
              </div>

              <button
                onClick={handleBook}
                className="px-6 py-3 rounded-full text-sm sm:text-base font-semibold text-white bg-woofsy-primary hover:bg-[#e8792c] shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all flex items-center gap-2 transform hover:-translate-y-0.5"
              >
                Book Doorstep Grooming <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer onBook={handleBook} />
    </div>
  );
}
